import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { createSelector, Store } from '@ngrx/store';
import * as moment from 'moment';
import { combineLatest, of, Observable, forkJoin, iif } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import {
  ConcreteProposalOP,
  COUNTRY_CODE,
  PlantsScheduleOP,
  ProductOP,
  SLOT_REQUEST_DCO_TYPES,
  SlotsResponseOP,
} from '@cemex-core/cmx-api';
import { SlotsOpenPlatformService, SlotsRequestOP, AdditionalParameters } from '@cemex-core/cmx-api';
import { CmxClientService, getAuth, getLegalEntity } from '@cemex/cmx-client-layout';
import { TranslateService } from '@ngx-translate/core';
import { AnalyticsTrackingService } from '@ordersnproducts/../services/analytics-tracking.service';

import {
  getFirstCalendar,
  getJobsiteState,
  getLocationPage,
  getPointOfDeliveryState,
  getShippingCondition,
  getSpecificationForm,
  OrderBuilderState,
  getActiveExperienceCode,
  getPointOfDeliveryAddress,
  getActiveExperienceCodeAndCountryCode,
  getActiveCountryCode,
  getCatalogsSelector,
} from '@ordersnproducts/modules/order-builder/reducers';
import {
  getRmsRecipeSpecifications,
  getRmsSpecificationFormState,
  getRmsSpecificationsIsModify,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/index';
import {
  getOpenPlatformProductAddedValues,
  getTemperatureVaps,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/added-value.selectors';
import {
  getAvailableProducts,
  getOpenPlatformProducts,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/products.selectors';
import {
  getCutOff,
  getCutOffTemperatureState,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/cut-off.selectors';

import { ORDER_EXPERIENCES } from '@ordersnproducts/modules/shared/constants/order-experiences';
import { RMS_REQUEST_TYPES } from '@ordersnproducts/utils';
import { CATALOG_CODE } from '@ordersnproducts/modules/shared/constants/catalog-codes';
import { CalendarType } from '@ordersnproducts/modules/shared/models/calendar-type.enum';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { isPump } from '@ordersnproducts/modules/shared/utils/pump';
import { Country, getAppInjector, makeReference } from '@ordersnproducts/utils/mix-reference';
import { ExperiencesService } from '@ordersnproducts/modules/shared/services/experiences.service';
import { generateAddedValueFormName } from '@ordersnproducts/modules/order-builder/modules/rms-specification/helpers/form-added-value-name-generator';

import * as fromDcoActions from '@ordersnproducts/modules/shared/actions/dco.actions';
import { getSlotForFormID } from '@ordersnproducts/modules/shared/reducers/index';
import { CwcTimeSlot } from '@ordersnproducts/modules/shared/models/dco/cwc-time-slot.model';
import { getLocalSpacings } from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/single-catalogs';
import IGeoPlace from '@cemex-core/cmx-api/models/geo-place/geo-place.interface';
import { getOrderSelectedDetail } from '@ordersnproducts/modules/new-modify-order/selectors/order-selected-detail.selector';
import { BusinessDay } from '@cemex-core/cmx-api/models/calendars/calendar';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Features } from '@ordersnproducts/features';
import { NO_AVAILABILITY_ERROR_CODE } from '../models/dco/order-status-modify-snapshot.model';
import { getRefrigerantValueAdded } from '@ordersnproducts/utils/temperature.util';
import { SHIPMENT_LOCATION_STATUS } from '../constants/shipment-location-status';
import { getPumpPrimerState } from '../modules/pump-primer/redux/pump-primer.selectors';
import {
  PUMP_PRIMER_ARRIVAL_BEFORE_DELIVERY,
  PUMP_PRIMER_MIX,
  PUMP_PRIMER_QUANTITY,
} from 'app/shared/pumping/pumping.constant';
import { prependedValueAdded } from '../utils/prepended-value-added';
import {
  isOrderWithPumpPrimerProduct,
  isUnloadTypePumpValue,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/helpers/pump-primer-helper';
import { ADDITIONAL_PARAMETERS } from '../constants/additional-parameters';
import {
  BOOM_PUMP,
  BOOM_PUMP_CODE,
  STATIONARY_PUMP,
  STATIONARY_PUMP_CODE,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/constants/pumps';
import { BusinessDayType } from '@ordersnproducts/utils/utils';
import { calculateCutOffIndicator } from '@ordersnproducts/utils/dco-utils';
import { RMXCOUNTRIES } from '@ordersnproducts/modules/order-builder/modules/rms-specification/models/rmx-ul-countries.constant';
import { Slot } from '../reducers/dco.reducer';

const recipeAndProducts = createSelector(
  getRmsRecipeSpecifications,
  getOpenPlatformProducts,
  getAvailableProducts,
  (recipe, availableProducts, commercialProducts) => {
    return { form: recipe, availableProducts, commercialProducts };
  },
);

export const largeOrderMessageConst = 'BIGO';

const pointOfDeliveryAndJobsite = createSelector(
  getPointOfDeliveryState,
  getJobsiteState,
  (pointOfDelivery, jobsite) => {
    return { pointOfDelivery, jobsite };
  },
);

@Injectable()
export class DcoEffect {
  constructor(
    private store: Store<OrderBuilderState>,
    private dcoActions$: Actions<fromDcoActions.Actions>,
    private alerts: CmxClientService,
    private translate: TranslateService,
    private analyticsService: AnalyticsTrackingService,
    private slotsApi: SlotsOpenPlatformService,
    private experiencesService: ExperiencesService,
    private featureToggle: FeatureToggleService,
  ) {
    //
  }
  injector = getAppInjector();
  country = Country.getInstance(this.injector);

  fetchSlots$ = createEffect(() =>
    this.dcoActions$.pipe(
      ofType(fromDcoActions.ActionTypes.FETCH_SLOTS),
      switchMap((action): Observable<Slot> => {
        return this.store.select(getSlotForFormID(action.payload.formId)).pipe(take(1));
      }),
      switchMap((slot) => {
        const technicalProduct$ = this.getLastTechnicalProduct(slot.formIndex).pipe(
          map((technicalProduct) => {
            // verify is technicalProduct is empty and replace with default object
            return (
              technicalProduct || {
                technicalProductXId: 0,
                technicalProductCode: '0',
                isPumpable: false,
              }
            );
          }),
          catchError((error) => {
            // in error case emit default object
            return of({
              technicalProductXId: 0,
              technicalProductCode: '0',
              isPumpable: false,
            });
          }),
        );
        const isBigOrder$ = this.isBigOrder(slot.formIndex);
        return combineLatest([technicalProduct$, isBigOrder$]).pipe(
          map(([technicalProduct, isBigOrder]) => {
            return { technicalProduct, isBigOrder, slot };
          }),
        );
      }),
      withLatestFrom(this.store.select(getActiveExperienceCode)),
      filter(([stateData, activeExperienceCode]) => {
        if (
          activeExperienceCode !== ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK &&
          activeExperienceCode !== ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK
        ) {
          const countryCodeString = this.country.getCountryCodeProperty() as COUNTRY_CODE;
          if (RMXCOUNTRIES.includes(countryCodeString)) {
            return true;
          }
          return !!stateData.technicalProduct && !stateData.isBigOrder;
        }
        return true;
      }),
      switchMap(([stateData, activeExperienceCode]) => {
        this.alerts.closeAllAlerts();
        this.alerts.addMainAlert('info', this.translate.instant('views.dco.loading'));
        return this.generateSlotsRequestParameter(
          stateData.slot.requestDatetime,
          stateData.slot.formIndex,
          activeExperienceCode,
        ).pipe(
          map((requestParameter) => {
            return { requestParameter: requestParameter, slot: stateData.slot, activeExperienceCode };
          }),
        );
      }),
      switchMap((data) => {
        return this.aggregateSlotRequestParameters(data);
      }),
      withLatestFrom(this.store.select(getRmsSpecificationFormState), this.store.select(getCutOff)),
      switchMap(([data, formState, cutOffState]) => {
        const recipe = formState.forms[data.data.slot.formIndex];
        const parameter = data.data.requestParameter;
        const isUsa = parameter.country.countryCode === COUNTRY_CODE.USA;
        const isMX = parameter.country.countryCode === COUNTRY_CODE.MEXICO;
        const defaultRank = isUsa ? 4 : 6;
        const nDays = _safe(() => data.calendar.offerDaysAfter, 2);
        const nDaysBefore = _safe(() => data.calendar.offerDaysBefore, 2);
        const nSlotsToRank = _safe(() => data.calendar.offerSlots, defaultRank);
        // update new values for the parameter object
        parameter.dcoType = SLOT_REQUEST_DCO_TYPES.OLS;
        parameter.products[0].availability.nDays = String(nDays);
        parameter.products[0].availability.nDaysBefore = nDaysBefore;
        parameter.products[0].availability.nSlotsToRank = nSlotsToRank;

        const experienceConfig = this.getExperienceConfiguration(data.data.activeExperienceCode);
        const apiConfig = this.getApiConfiguration(isUsa, isMX, experienceConfig);

        this.processArkikConfiguration(parameter, experienceConfig.isArkik);
        this.processTemperatureControl(parameter, recipe, apiConfig.useV2SlotApi, experienceConfig, data, formState);
        this.processPumpPrimerConfiguration(parameter, recipe, data);
        this.processPouringMethodConfiguration(parameter, recipe, data);
        this.processCutOffConfiguration(parameter, isUsa, apiConfig.useV2SlotApi, data, cutOffState);
        this.processReturnLoads(parameter, isMX, recipe);

        const enabledBusinessDays = this.featureToggle.isFeatureEnabled(Features.EnabledBusinessDays);
        const enableOutOfServiceHours = this.featureToggle.isFeatureEnabled(Features.OUT_OF_SERVICE_HOURS);

        // Extract variables for backward compatibility
        const isCommand = experienceConfig.isCommand;
        const useV2SlotApi = apiConfig.useV2SlotApi;

        const slotObservable = experienceConfig.isArkik
          ? this.slotsApi.slotsArkik(parameter)
          : this.slotsApi.post(parameter, useV2SlotApi);
        return slotObservable.pipe(
          map((callbackResponse: SlotsResponseOP) => {
            this.store.dispatch(new fromDcoActions.SaveDcoIds(callbackResponse.prefixHeader));
            this.store.dispatch(new fromDcoActions.SaveResponseMessage(callbackResponse.responseMessage));
            this.store.dispatch(new fromDcoActions.SaveExceptionMessage(callbackResponse.exceptionMessage));
            this.store.dispatch(new fromDcoActions.SaveCallDCO(callbackResponse.callDCO));

            this.store.dispatch(
              new fromDcoActions.FetchSlotsBigo({
                isBigOrder: callbackResponse.responseMessage === largeOrderMessageConst,
                message: callbackResponse.responseMessage,
                resetLargeOrder: false,
              }),
            );

            if (enableOutOfServiceHours) {
              this.store.dispatch(new fromDcoActions.ResetOutOfServiceSlots());
            }

            const slot = _safe(() => callbackResponse.slots[0]);
            const slotApiResponse = callbackResponse;

            if (_safe(() => slot.concreteProposals.length)) {
              // filter data
              slotApiResponse.slots[0].plantsSchedule = this.filterPlantsScheduleDataWithRangeLimits(
                callbackResponse.slots[0].plantsSchedule,
                data.OfferDay.cutOffString,
                enabledBusinessDays && isCommand ? data.calendar.businessDays.length - 1 : data.OfferDay.offerDays,
                enabledBusinessDays && isCommand
                  ? data.calendar.businessDays[data.calendar.businessDays.length - 1].date.toString()
                  : null,
              );
              slotApiResponse.slots[0].concreteProposals = this.filterConcreteProposalsDataWithRangeLimits(
                callbackResponse.slots[0].concreteProposals,
                data.OfferDay.cutOffString,
                enabledBusinessDays && isCommand ? data.calendar.businessDays.length - 1 : data.OfferDay.offerDays,
                enabledBusinessDays && isCommand
                  ? data.calendar.businessDays[data.calendar.businessDays.length - 1].date.toString()
                  : null,
              );
              return new fromDcoActions.FetchSlotsSuccess({
                formId: data.data.slot.formId,
                response: slotApiResponse,
                formSnapshot: recipe,
              });
            }

            const formId = _safe(() => data.data.slot.formId);
            const error = _safe(() => {
              if (callbackResponse && callbackResponse.callDCO) {
                if (slot && slot.requestProductStatus) {
                  return slot.requestProductStatus;
                } else {
                  return {
                    callDco: callbackResponse.callDCO,
                    httpStatusReasonCode: NO_AVAILABILITY_ERROR_CODE,
                  };
                }
              }
              return { callDco: null, httpStatusReasonCode: NO_AVAILABILITY_ERROR_CODE };
            });

            const formSnapshot = _safe(() => recipe);

            return new fromDcoActions.FetchSlotsError({
              formId: formId,
              error: error,
              formSnapshot: formSnapshot,
            });
          }),
          catchError((err) => {
            this.store.dispatch(
              new fromDcoActions.FetchSlotsBigo({
                isBigOrder: false,
                message: null,
                resetLargeOrder: false,
              }),
            );

            if (_safe(() => err.error.prefixHeader)) {
              this.store.dispatch(new fromDcoActions.SaveDcoIds(err.error.prefixHeader));
            }

            this.analyticsService.noSlotAvailableReport();

            return of(
              new fromDcoActions.FetchSlotsError({
                formId: data.data.slot.formId,
                error: _safe(() => (!!err.error ? err.error : err)),
                formSnapshot: _safe(() => recipe),
              }),
            );
          }),
        );
      }),
    ),
  );

  saveDcoIds$ = createEffect(() =>
    this.dcoActions$.pipe(
      ofType(fromDcoActions.ActionTypes.FETCH_SLOTS_SUCCESS),
      map((action) => {
        return new fromDcoActions.SaveDcoIds(action.payload.response.slots[0].header.prefix);
      }),
    ),
  );

  /**
   * After the success of the data fetch, verify is requested moment is available as a slot
   */

  fetchSlotsSuccess$ = createEffect(() =>
    this.dcoActions$.pipe(
      ofType(fromDcoActions.ActionTypes.FETCH_SLOTS_SUCCESS),
      switchMap((action) => this.store.select(getSlotForFormID(action.payload.formId)).pipe(take(1))),
      filter((slot) => !!slot),
      map((slot) => {
        const deliveryDate = slot.requestDatetime.format('YYYY-MM-DD');
        const arriveAt = slot.requestDatetime.format('HH:mm');
        const availableSlot = slot.response.slots[0].concreteProposals.some(
          (proposal) => proposal.deliveryDate === deliveryDate && proposal.arriveAtText === arriveAt,
        );
        this.alerts.closeAllAlerts();
        this.alerts.addMainAlert('success', this.translate.instant('views.dco.loading_success'));
        if (availableSlot) {
          // Requested moment coincides automatically with an available date
          const slotDetail = slot.response.slots[0].concreteProposals.find(
            (proposal) => proposal.deliveryDate === deliveryDate && proposal.arriveAtText === arriveAt,
          );
          this.store.dispatch(new fromDcoActions.SetSelectedSlot(slotDetail));
          return new fromDcoActions.MarkSlotAvailable({ formId: slot.formId, isAvailable: true });
        }
        const enableOutOfServiceHours = this.featureToggle.isFeatureEnabled(Features.OUT_OF_SERVICE_HOURS);
        if (enableOutOfServiceHours) {
          const active = !!slot.response['outOfServiceHours'];
          this.store.dispatch(new fromDcoActions.SaveOutOfServiceSlots({ formId: slot.formId, active }));
        }
        // Requested moment is not available, but there are proposals
        // A manual selection from the user is required
        return new fromDcoActions.MarkSlotAvailable({ formId: slot.formId, isAvailable: false });
      }),
    ),
  );

  fetchSlotsError = createEffect(
    () =>
      this.dcoActions$.pipe(
        ofType(fromDcoActions.ActionTypes.FETCH_SLOTS_ERROR),
        tap((action) => {
          console.warn('Fetch slots error response');
        }),
      ),
    { dispatch: false },
  );

  /** **/

  removeItem$ = createEffect(() =>
    this.dcoActions$.pipe(
      ofType(fromDcoActions.ActionTypes.REMOVE_ITEM),
      map((action) => {
        return new fromDcoActions.RemoveItemSuccess();
      }),
    ),
  );

  formatDataForCalendarView$ = createEffect(() =>
    this.dcoActions$.pipe(
      ofType(fromDcoActions.ActionTypes.FORMAT_DATA_FOR_CALENDAR_VIEW),
      switchMap((action) => {
        return this.store.select(getSlotForFormID(action.payload.formId)).pipe(
          take(1),
          map((slot) => {
            return { slot, action };
          }),
        );
      }),
      switchMap(({ slot, action }) => {
        return this.getCutOffAndOfferDays().pipe(
          take(1),
          map((response) => {
            const cutOffString = response.cutOffString;
            const offerDays = response.offerDays;
            const businnesDays = response.businessDays;
            return { slot, action, cutOffString, offerDays, businnesDays };
          }),
        );
      }),
      map(({ slot, action, cutOffString, offerDays, businnesDays }) => {
        const data = slot.response.slots[0];
        const plantsSchedule = data.plantsSchedule;
        const concreteProposals = data.concreteProposals;
        const timeSlotsData = this.formatDataForFullScheduleVisibility(
          plantsSchedule,
          concreteProposals,
          cutOffString,
          offerDays,
          businnesDays,
        );
        return new fromDcoActions.PushTimeSlots({ formId: action.payload.formId, timeslots: timeSlotsData });
      }),
    ),
  );

  fetchScheduleDays$ = createEffect(() =>
    this.dcoActions$.pipe(
      ofType(fromDcoActions.ActionTypes.FETCH_SCHEDULE_DAYS),
      switchMap((action) => {
        return this.store.select(getSlotForFormID(action.payload.formId)).pipe(
          take(1),
          map((slot) => {
            return { slot, action };
          }),
        );
      }),
      switchMap(({ slot, action }) => {
        const technicalProduct$ = this.getLastTechnicalProduct(slot.formIndex);
        const isBigOrder$ = this.isBigOrder(slot.formIndex);
        return combineLatest([technicalProduct$, isBigOrder$]).pipe(
          map(([technicalProduct, isBigOrder]) => {
            return { technicalProduct, isBigOrder, slot, action };
          }),
        );
      }),
      withLatestFrom(this.store.select(getActiveExperienceCode)),
      // tap(([stateData, activeExperienceCode]) => {
      // if (!stateData.technicalProduct && activeExperienceCode !== ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK) {
      //     this.alerts.closeAllAlerts();
      //     this.alerts.addMainAlert('warning', this.translate.instant('views.rms_online.invalid_technical_product'));
      //     this.store.dispatch(new fromSlotsActions.SlotReset(stateData.action.payload.formIndex));
      // } else if (stateData.isBigOrder && activeExperienceCode !== ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK) {
      // si es big order se debe cambiar a que el componente lo verifique
      // si es verdadero, que NO EJECUTE EL CALLBACK A API
      // this.store.dispatch(new fromSlotsActions.SlotReset(stateData.action.payload.formIndex));
      // } else {
      //     this.alerts.closeAllAlerts();
      //     this.alerts.addMainAlert('info', this.translate.instant('views.rms_online.loading_slots'));
      // }
      // }),
      filter(([stateData, activeExperienceCode]) => {
        if (
          activeExperienceCode !== ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK &&
          activeExperienceCode !== ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK
        ) {
          return !!stateData.technicalProduct && !stateData.isBigOrder;
        }
        return true;
      }),
      switchMap(([stateData, activeExperienceCode]) => {
        return this.generateSlotsRequestParameter(
          stateData.slot.requestDatetime,
          stateData.slot.formIndex,
          activeExperienceCode,
        ).pipe(
          map((requestParameter) => {
            return {
              requestParameter: requestParameter,
              slot: stateData.slot,
              activeExperienceCode,
              action: stateData.action,
            };
          }),
          switchMap((data) => {
            return this.aggregateSlotRequestParameters(data).pipe(
              map((aggResult) => {
                return { aggResult, action: data.action };
              }),
            );
          }),
        );
      }),
      withLatestFrom(this.store.select(getRmsSpecificationFormState), this.store.select(getCutOff)),
      switchMap(([aggResult, formState, cutOff]) => {
        // aggResult is the object returned by aggregateSlotRequestParameters
        // It has: OfferDay, calendar, data, vaps, isOrderWithPumpPrimer, pumpPrimerState
        const { data } = aggResult.aggResult ?? {};
        // data contains: requestParameter, slot, activeExperienceCode, action
        const { requestParameter, slot, action } = data;
        return this.getCutOffAndOfferDays().pipe(
          take(1),
          map((response) => {
            const cutOffString = response.cutOffString;
            const offerDays = response.offerDays;
            const businessDays = response.businessDays;
            return {
              requestParameter,
              slot,
              action,
              cutOffString,
              offerDays,
              businessDays,
              otherData: aggResult.aggResult,
              formState,
              cutOffState: cutOff,
            };
          }),
        );
      }),
      switchMap(
        ({
          requestParameter,
          slot,
          action,
          cutOffString,
          offerDays,
          businessDays,
          otherData,
          formState,
          cutOffState,
        }) => {
          const parameter = requestParameter;
          parameter.dcoType = SLOT_REQUEST_DCO_TYPES.LEGACY;
          const requestDatetime = action.payload.requestDatetime;
          const selectedDateTimeAsString = requestDatetime.format('YYYY-MM-DD HH:mm');
          parameter.products[0].availability.requestedDateTime = selectedDateTimeAsString;
          parameter.products[0].availability.searchForDateTime = selectedDateTimeAsString;
          let nDays = 2;
          if (requestDatetime.day() === 5) {
            // request date is a friday, include next monday
            nDays = 3;
          }
          parameter.products[0].availability.nDays = String(nDays);
          const recipe = formState.forms[otherData.data.slot.formIndex];
          const isUsa = parameter.country.countryCode === COUNTRY_CODE.USA;
          const isMX = parameter.country.countryCode === COUNTRY_CODE.MEXICO;
          const experienceConfig = this.getExperienceConfiguration(otherData.data.activeExperienceCode);
          // TODO: MX needs to develop logic for repeat orders
          const apiConfig = this.getApiConfiguration(isUsa, isMX, experienceConfig);
          const useV2SlotApi = apiConfig.useV2SlotApi;
          if (useV2SlotApi) {
            // update new values for the parameter object
            const nDaysBefore = _safe(() => otherData.calendar.offerDaysBefore, 2);
            const nSlotsToRank = _safe(() => otherData.calendar.offerSlots, 100);
            // update new values for the parameter object
            parameter.dcoType = SLOT_REQUEST_DCO_TYPES.OLS;
            parameter.products[0].availability.nDays = String(nDays);
            parameter.products[0].availability.nDaysBefore = nDaysBefore;
            parameter.products[0].availability.nSlotsToRank = nSlotsToRank;
          }

          this.processArkikConfiguration(parameter, experienceConfig.isArkik);
          this.processTemperatureControl(parameter, recipe, useV2SlotApi, experienceConfig, otherData, formState);
          this.processPumpPrimerConfiguration(parameter, recipe, otherData);
          this.processPouringMethodConfiguration(parameter, recipe, otherData);
          this.processReturnLoads(parameter, isMX, recipe);
          this.processCutOffConfiguration(parameter, isUsa, useV2SlotApi, otherData, cutOffState);

          const slotObservable = experienceConfig.isArkik
            ? this.slotsApi.slotsArkik(parameter)
            : this.slotsApi.post(parameter, useV2SlotApi);
          return slotObservable.pipe(
            map((callbackResponse) => {
              const slotResponse = callbackResponse.slots[0];
              const plantsSchedule = slotResponse.plantsSchedule;
              const concreteProposals = slotResponse.concreteProposals;
              const cwcTimeSlots = this.formatDataForFullScheduleVisibility(
                plantsSchedule,
                concreteProposals,
                cutOffString,
                offerDays,
                businessDays,
              );
              if (cwcTimeSlots.length > 0) {
                return new fromDcoActions.FetchScheduleDaysSuccess({
                  formId: slot.formId,
                  response: cwcTimeSlots,
                });
              }
              return new fromDcoActions.FetchScheduleDaysError({
                formId: slot.formId,
                error: slotResponse.requestProductStatus,
              });
            }),
            catchError((err) => {
              this.analyticsService.noSlotAvailableReport();
              return of(
                new fromDcoActions.FetchScheduleDaysError({
                  formId: slot.formId,
                  error: _safe(() => (!!err.error ? err.error : err)),
                }),
              );
            }),
          );
        },
      ),
    ),
  );

  fetchScheduleDaysSuccess$ = createEffect(() =>
    this.dcoActions$.pipe(
      ofType(fromDcoActions.ActionTypes.FETCH_SCHEDULE_DAYS_SUCCESS),
      switchMap((action) => {
        return this.store.select(getSlotForFormID(action.payload.formId)).pipe(
          take(1),
          map((slot) => {
            return { slot, action };
          }),
        );
      }),
      map(({ slot, action }) => {
        let cwcTimeSlots = action.payload.response;
        const dayNamesString: string = this.translate.instant('global.dayNames');
        const dayNames = dayNamesString.split(',');
        const countryCodeString = this.country.getCountryCodeProperty() as COUNTRY_CODE;
        const displayDateFormat = countryCodeString === COUNTRY_CODE.UNITED_KINGDOM ? 'DD MMM, YYYY' : 'MMM DD, YYYY';
        // remove repeating days
        cwcTimeSlots.forEach((timeSlot, index) => {
          const foundIndex = slot.timeSlots.findIndex((item) => item.dateMoment.isSame(timeSlot.dateMoment, 'day'));
          if (foundIndex > -1) {
            cwcTimeSlots.splice(index, 1);
          }
        });
        if (cwcTimeSlots.length > 0) {
          let auxItem = cwcTimeSlots[cwcTimeSlots.length - 1];
          let limitItem = slot.timeSlots[0];
          if (auxItem.dateMoment.isBefore(limitItem.dateMoment, 'day')) {
            // fill gaps
            const daysDiff = limitItem.dateMoment.diff(auxItem.dateMoment, 'days');
            if (daysDiff > 1) {
              let daysToSum = 1;
              while (daysToSum < daysDiff) {
                const missingDate = auxItem.dateMoment.clone().add(daysToSum, 'days');
                const dayDisplayString = dayNames[missingDate.day()];
                const newTimeSlot: CwcTimeSlot = {
                  dateMoment: missingDate,
                  dayDisplay: dayDisplayString,
                  dateDisplay: missingDate.format(displayDateFormat),
                  plantOpeningTime: missingDate,
                  plantClosingTime: missingDate,
                  schedule: [],
                };
                cwcTimeSlots.push(newTimeSlot);
                daysToSum++;
              }
            }
            // add elements at the beginning of the array
            return new fromDcoActions.UnshiftTimeSlots({ formId: action.payload.formId, timeslots: cwcTimeSlots });
          }
          // fill gaps
          auxItem = slot.timeSlots[slot.timeSlots.length - 1];
          limitItem = cwcTimeSlots[0];
          const daysGap = limitItem.dateMoment.diff(auxItem.dateMoment, 'days');
          if (daysGap > 1) {
            let daysToSum = 1;
            const newArray = [];
            while (daysToSum < daysGap) {
              const missingDate = auxItem.dateMoment.clone().add(daysToSum, 'days');
              const dayDisplayString = dayNames[missingDate.day()];
              const newTimeSlot: CwcTimeSlot = {
                dateMoment: missingDate,
                dayDisplay: dayDisplayString,
                dateDisplay: missingDate.format(displayDateFormat),
                plantOpeningTime: missingDate,
                plantClosingTime: missingDate,
                schedule: [],
              };
              newArray.push(newTimeSlot);
              daysToSum++;
            }
            cwcTimeSlots = newArray.concat(cwcTimeSlots);
          }
          // add elements to the end of the array
          return new fromDcoActions.PushTimeSlots({ formId: action.payload.formId, timeslots: cwcTimeSlots });
        }
        return new fromDcoActions.FetchScheduleDaysError({
          formId: action.payload.formId,
          error: 'No new days',
        });
      }),
    ),
  );

  fetchScheduleDaysError$ = createEffect(
    () =>
      this.dcoActions$.pipe(
        ofType(fromDcoActions.ActionTypes.FETCH_SCHEDULE_DAYS_ERROR),
        tap((action) => {
          console.log(' Error FULL Schedule DAYS');
        }),
      ),
    { dispatch: false },
  );

  private aggregateSlotRequestParameters(data: {
    requestParameter: SlotsRequestOP;
    slot: Slot;
    activeExperienceCode: string;
    [key: string]: any;
  }) {
    const calendar$ = this.store.select(getFirstCalendar).pipe(
      filter((calendar) => !!calendar),
      take(1),
    );
    const vapsTemperature$ = this.store.select(getTemperatureVaps);
    const isOrderWithPumpPrimer$ = this.isOrderWithPumpPrimer(data.slot.formIndex);
    const getPumpPrimerState$ = this.store.select(getPumpPrimerState);
    return combineLatest([
      this.getCutOffAndOfferDays(),
      calendar$,
      vapsTemperature$,
      isOrderWithPumpPrimer$,
      getPumpPrimerState$,
    ]).pipe(
      take(1),
      map(([OfferDay, calendar, vaps, isOrderWithPumpPrimer, pumpPrimerState]) => {
        return { OfferDay, calendar, data, vaps, isOrderWithPumpPrimer, pumpPrimerState };
      }),
    );
  }

  removeLeadingZeros(param) {
    return param.startsWith('00') ? param.replace(/^00/, '') : param;
  }

  /**
   *
   * @param fromMoment
   * @param formIndex
   * @param activeExperienceCode
   * @returns
   */
  private generateSlotsRequestParameter(
    fromMoment: moment.Moment,
    formIndex: number,
    activeExperienceCode: string,
  ): Observable<SlotsRequestOP> {
    const searchSlotRequest$ = this.getCutOff().pipe(
      withLatestFrom(this.getSelectedProduct(formIndex)),
      map(([cutoff, selectedProduct]) => {
        let cutOffAsString: string = moment(cutoff).format('YYYY-MM-DD HH:mm');
        if (
          activeExperienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK ||
          activeExperienceCode === ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK
        ) {
          cutOffAsString = moment().add(1, 'day').format('YYYY-MM-DD 00:00');
        }
        const selectedDateTimeAsString = fromMoment.format('YYYY-MM-DD HH:mm');
        return { cutOffAsString, selectedDateTimeAsString, selectedProduct };
      }),
      switchMap((pipeState) => {
        return forkJoin({
          nDays: this.nDays(fromMoment, pipeState.cutOffAsString),
          requestType: this.requestType(formIndex),
          pump: this.pump(formIndex),
          selectedAddedValues: this.getSelectedAddedValues(formIndex),
          productCodes: this.productCodes(formIndex, activeExperienceCode),
          loadSize: this.selectLoadSize(formIndex),
          volume: this.volume(formIndex),
          deliveryFrequency: this.deliveryFrequency(formIndex),
          unloadingTime: this.unloadingTime(formIndex),
          jobsiteCode: this.getJobsiteCode(),
          deliveryPoint: this.deliveryPoint(),
          salesOrgCode: this.salesOrgCode(),
          countryCode: this.countryCode(),
          username: this.username(),
          shippingConditions: this.shippingConditions(),
          address: this.deliveryAddress(),
          orderCode: this.getOrderCode(),
          isModifyOrder: this.isModifyOrder(),
          programmedDateTime: this.getProgrammedDateTime(),
          technicalProduct: this.getTechnicalProduct(formIndex),
        }).pipe(
          map((response) => {
            const orderCode =
              (response.countryCode === COUNTRY_CODE.USA ||
                response.countryCode === COUNTRY_CODE.UNITED_KINGDOM ||
                response.countryCode === COUNTRY_CODE.MEXICO) &&
              response.isModifyOrder
                ? response.orderCode
                : 0;
            const services = Math.ceil(response.volume / response.loadSize);
            const aux: SlotsRequestOP = {
              orderCode: Number(orderCode),
              jobsite: {
                jobsiteCode: response.jobsiteCode,
              },
              pointOfDelivery: {
                pointOfDeliveryCode: String(response.deliveryPoint),
              },
              address: {
                geoPlace: {
                  latitude: response.address.latitude,
                  longitude: response.address.longitude,
                },
              },
              salesOrganization: {
                salesOrganizationCode: response.salesOrgCode,
              },
              country: {
                countryCode: response.countryCode,
              },
              products: [
                {
                  productIndex: 0,
                  productCode: this.getProductCode(
                    response.technicalProduct,
                    pipeState.selectedProduct,
                    response.countryCode,
                    activeExperienceCode,
                  ),
                  availability: {
                    searchForDateTime: pipeState.selectedDateTimeAsString,
                    requestedDateTime: pipeState.selectedDateTimeAsString,
                    requestType: response.requestType,
                    plantSchedule: true,
                    nDays: String(response.nDays),
                  },
                  volume: response.volume,
                  loadCapacity: response.loadSize,
                  numOfServices: services,
                  deliveryFrequency: response.deliveryFrequency,
                  shippingConditions: response.shippingConditions,
                  unloadingTime: response.unloadingTime,
                  pump: response.pump,
                  valueAddedCodes: response.productCodes,
                },
              ],
              application: {
                user: {
                  userName: response.username,
                },
              },
            };

            if (response.programmedDateTime) {
              aux.programmedDateTime = response.programmedDateTime;
            }

            if (response.deliveryPoint !== 0) {
              delete aux.address;
            }
            return aux;
          }),
        );
      }),
    );
    return searchSlotRequest$;
  }

  /**
   * Retrieves the technical product code based on the provided form index.
   *
   * This method first attempts to get the technical product directly. If it is not available,
   * it will then attempt to find the technical product from the available open platform products
   * based on the recipe specifications.
   *
   * @param {number} formIndex - The index of the form to retrieve the technical product for.
   * @returns {Observable<string>} An observable that emits the technical product code.
   */
  private getTechnicalProduct(formIndex: number): Observable<string> {
    return this.technicalProduct(formIndex).pipe(
      withLatestFrom(this.store.select(getRmsRecipeSpecifications).pipe(filter((state) => !!state.length))),
      switchMap(([technicalProduct, recipe]) => {
        return iif(
          () => !!technicalProduct,
          // if true
          of(technicalProduct),
          // if false
          this.store.select(getOpenPlatformProducts).pipe(
            map((availables) =>
              availables.find(
                (available) => makeReference(available) === recipe[0].values[SpecFieldNames.PRODUCT_SELECTION],
              ),
            ),
            map((productSelected) => productSelected.technicalProducts),
            map((technicals) => {
              const unloadTypeValue = recipe[0].values[SpecFieldNames.UNLOAD_TYPE];
              const hasPump = isPump(unloadTypeValue);
              // If the user selects Pump, needs a pumpable technical product.
              if (!!hasPump) {
                return _safe(
                  () =>
                    technicals && technicals.find((technical) => technical.isPumpable === hasPump).technicalProductCode,
                );
              }
              // If the user does not selects Pump,
              // Select the first technical product not pumpable as default.
              return _safe(
                () => technicals && technicals.find((technical) => technical.isPumpable === false).technicalProductCode,
              );
            }),
            take(1),
          ),
        );
      }),
    );
  }

  /**
   * Get the product code of the commercial product
   * but for RMX UL the technical product code is used
   * @param technicalProduct
   * @param selectedProduct
   * @param countryCode
   * @returns string
   */
  private getProductCode(technicalProduct, selectedProduct, countryCode, activeExperienceCode) {
    if (COUNTRY_CODE.MEXICO === countryCode || activeExperienceCode === ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK) {
      return technicalProduct ? technicalProduct : selectedProduct.productCode;
    }
    return selectedProduct.productCode;
  }

  /**
   *
   * @param formIndex
   * @returns
   */
  private getSelectedAddedValues(formIndex: number): Observable<string[]> {
    const addedValues$ = this.store.select(getRmsRecipeSpecifications).pipe(
      map((recipes) => {
        const addedValues: string[] = [];
        const recipe = recipes[formIndex].values;
        for (let i = 0; i < 4; i++) {
          const aux = recipe[generateAddedValueFormName(i)];
          if (aux !== undefined) {
            addedValues.push(aux);
          }
        }
        return addedValues;
      }),
      take(1),
    );
    return addedValues$;
  }

  /**
   * Provides [nDays] api field
   */

  /**
   * Gets the product selected by the user in the product form in the RMS specification step
   * @param formIndex
   * @returns An Observable with the open platform product
   */
  private getSelectedProduct(formIndex: number): Observable<ProductOP> {
    return this.store.select(recipeAndProducts).pipe(
      withLatestFrom(this.countryCode()),
      map(([{ form, availableProducts, commercialProducts }, countryCode]) => {
        const recipe = form[formIndex].values;
        const productCode: string = recipe[SpecFieldNames.PRODUCT_SELECTION];
        if (countryCode === COUNTRY_CODE.COLOMBIA) {
          const commProduct = commercialProducts.find((p) => {
            return p.comm_product.comm_product === productCode;
          });
          // we only need a productCode further, hence return productCode only;
          return { productCode: commProduct.comm_product.comm_product } as ProductOP;
        }
        const product = availableProducts.find((availableProduct) => {
          if ([COUNTRY_CODE.UNITED_KINGDOM, COUNTRY_CODE.FRANCE].includes(countryCode as COUNTRY_CODE)) {
            return makeReference(availableProduct) === productCode;
          } else {
            return availableProduct.productCode.replace(/^0+/, '') === productCode;
          }
        });
        return product;
      }),
    );
  }

  /**
   * Returns the number of days to be considered from the cutoff day value depending of the calendar type
   * @param fromMoment
   * @param cutOff
   * @returns The value of offer days as a number
   */
  private nDays(fromMoment?: moment.Moment, cutOff?: string): Observable<number> {
    return this.store.select(getLocationPage).pipe(
      map((state) => {
        let calendarType = String(CalendarType.MIXTO);
        if (state.calendars && state.calendars.calendars && state.calendars.calendars.length > 0) {
          calendarType = state.calendars.calendars[0].calendarType;
        }
        // The segmentation B just load slots for 2 days
        const offerDaysSegmentationB = 2;
        if (calendarType === CalendarType.SMALL) {
          return offerDaysSegmentationB;
        } else {
          // Should rest one day because the offert should be considered from the cutOff day and not from today
          const offerDays = _safe(() => state.calendars.calendars[0].offerDays, 1);
          return offerDays;
        }
      }),
      take(1),
    );
  }

  /**
   *
   * @returns
   */
  private shipToCode(): Observable<number> {
    return this.store.select(getJobsiteState).pipe(
      map(({ selected }) => Number(selected.shipmentLocationCode)),
      take(1),
    );
  }

  /**
   *
   * @returns
   */
  private getJobsiteCode(): Observable<string> {
    const jobsiteCode$ = this.store.select(getJobsiteState).pipe(
      take(1),
      map((response) => {
        return response.selected.shipmentLocationCode;
      }),
    );
    return jobsiteCode$;
  }

  /**
   *
   * @returns
   */
  private deliveryPoint(): Observable<number> {
    return this.store.select(pointOfDeliveryAndJobsite).pipe(
      switchMap(({ pointOfDelivery, jobsite }) => {
        return this.shipToCode().pipe(
          map((shipCode) => {
            const selected = pointOfDelivery.selected;
            if (selected == null) {
              return shipCode;
            }
            if (
              _safe(() => selected.shipmentLocationStatus.shipmentLocationStatusCode) !==
                SHIPMENT_LOCATION_STATUS.ACTIVE &&
              selected.shipmentLocationCode !== jobsite.selected.shipmentLocationCode
            ) {
              return Number(0);
            }
            return Number(selected.shipmentLocationCode);
          }),
        );
      }),
      take(1),
    );
  }

  /**
   *
   * @returns
   */
  private deliveryAddress(): Observable<IGeoPlace> {
    const address$ = this.store.select(getPointOfDeliveryAddress).pipe(
      take(1),
      map((response) => {
        return !!response ? response.geoPlace : { latitude: 0, longitude: 0 };
      }),
    );
    return address$;
  }

  /**
   *
   * @param formIndex
   * @returns
   */
  private selectLoadSize(formIndex: number): Observable<number> {
    const loadSize$ = this.experiencesService.commandDcoEnabled$().pipe(
      withLatestFrom(this.countryCode()),
      switchMap(([isCommandDcoEnabled, countryCode]) => {
        const isColombia = countryCode === COUNTRY_CODE.COLOMBIA;
        if (isCommandDcoEnabled || isColombia) {
          return this.loadSizeCommand(formIndex);
        }
        return this.loadCapacity();
      }),
      take(1),
    );
    return loadSize$;
  }

  /**
   *
   * @returns
   */
  private loadCapacity(): Observable<number> {
    return this.store.select(getLocationPage).pipe(
      map((state) => {
        let loadSize = 10;
        if (state.calendars && state.calendars.calendars && state.calendars.calendars.length > 0) {
          loadSize = state.calendars.calendars[0].loadSize;
        }
        return loadSize;
      }),
      take(1),
    );
  }

  /**
   *
   * @param formIndex
   * @returns
   */
  private loadSizeCommand(formIndex: number): Observable<number> {
    return this.store.select(getRmsRecipeSpecifications).pipe(
      filter((state) => !!state.length),
      map((form) => {
        return form[formIndex].values;
      }),
      withLatestFrom(this.countryCode()),
      map(([recipe, countryCode]) => {
        const measureUnit = countryCode !== COUNTRY_CODE.COLOMBIA ? 'yd3' : 'm3';
        const aux = recipe[SpecFieldNames.LOAD_SIZE].replace(measureUnit, '');
        return Number(aux);
      }),
      take(1),
    );
  }

  /**
   *
   * @param formIndex
   * @returns
   */
  private volume(formIndex: number): Observable<number> {
    return this.store.select(getRmsRecipeSpecifications).pipe(
      filter((state) => !!state.length),
      map((form) => form[formIndex].values),
      map((recipe) => parseFloat(recipe[SpecFieldNames.QUANTITY])),
      take(1),
    );
  }

  /**
   *
   * @param formIndex
   * @returns
   */
  private productCodes(formIndex: number, activeExperienceCode: string): Observable<string[]> {
    return this.countryCode().pipe(
      switchMap((countryCode) => {
        const commProduct$ = iif(
          () => [COUNTRY_CODE.UNITED_KINGDOM, COUNTRY_CODE.FRANCE].includes(countryCode as COUNTRY_CODE),
          // if true
          this.commProductMixReference(formIndex),
          // if false
          this.commProduct(formIndex),
        );
        const addedValues$ = iif(
          () => [COUNTRY_CODE.UNITED_KINGDOM].includes(countryCode as COUNTRY_CODE),
          // if true
          this.addedValuesMixReference(formIndex),
          // if false
          this.addedValues(formIndex),
        );
        return forkJoin({
          commProduct: commProduct$,
          technicalProduct: this.technicalProduct(formIndex),
          addedValues: addedValues$,
        }).pipe(
          map((response) => {
            const aux = [];
            // RMX UL 5.2 do not have to send the technical product as value added
            if (
              response.technicalProduct &&
              countryCode !== COUNTRY_CODE.MEXICO &&
              activeExperienceCode !== ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK
            ) {
              aux.push(response.technicalProduct);
            }
            response.addedValues.forEach((value) => {
              aux.push(value);
            });
            return aux;
          }),
        );
      }),
    );
  }

  /**
   *
   * @param formIndex
   * @returns
   */
  private commProduct(formIndex: number): Observable<string> {
    return this.store.select(getRmsRecipeSpecifications).pipe(
      filter((state) => !!state.length),
      map((form) => form[formIndex].values),
      map((recipe) => recipe[SpecFieldNames.PRODUCT_SELECTION]),
      withLatestFrom(this.store.select(getActiveCountryCode)),
      switchMap(([code, countryCode]) => {
        const commercialProducts = this.store.select(getAvailableProducts).pipe(
          map((prods) =>
            prods.map((prod) => {
              return {
                productCode: prod.comm_product.comm_product,
              };
            }),
          ),
        );
        const source =
          countryCode !== COUNTRY_CODE.COLOMBIA ? this.store.select(getOpenPlatformProducts) : commercialProducts;
        return source.pipe(
          map((products) => products.find((product) => product.productCode.replace(/^0+/, '') === code)),
          take(1),
        );
      }),
      map((product) => product.productCode),
      take(1),
    );
  }

  /**
   *
   * @param formIndex
   * @returns
   */
  private commProductMixReference(formIndex: number): Observable<string> {
    return this.store.select(getRmsRecipeSpecifications).pipe(
      filter((state) => !!state.length),
      map((form) => form[formIndex].values),
      map((recipe) => recipe[SpecFieldNames.PRODUCT_SELECTION]),
      switchMap((code) =>
        this.store.select(getOpenPlatformProducts).pipe(
          map((products) => products.find((available) => makeReference(available) === code)),
          take(1),
        ),
      ),
      map((product) => product.productCode),
      take(1),
    );
  }

  /**
   *
   * @param formIndex
   * @returns
   */
  private technicalProduct(formIndex: number): Observable<string> {
    return this.countryCode().pipe(
      switchMap((countryCode) => {
        return iif(
          () => countryCode === COUNTRY_CODE.UNITED_KINGDOM,
          // if true
          this.lastTechnicalProductMixReference(formIndex).pipe(
            map((technicalProductMixReference) => {
              return technicalProductMixReference && technicalProductMixReference.technicalProductCode;
            }),
          ),
          // if false
          this.lastTechnicalProduct(formIndex).pipe(
            map((technicalProduct) => {
              return technicalProduct && technicalProduct.technicalProductCode;
            }),
          ),
        );
      }),
    );
  }

  /**
   * Provides last [technicalProduct]
   */
  private getLastTechnicalProduct(formIndex: number) {
    return this.store.select(getLegalEntity).pipe(
      map((legalEntity) => legalEntity.countryCode.trim()),
      take(1),
      switchMap((countryCode) => {
        if (countryCode === COUNTRY_CODE.UNITED_KINGDOM) {
          return this.lastTechnicalProductMixReference(formIndex);
        }
        return this.lastTechnicalProduct(formIndex);
      }),
    );
  }

  /**
   * Provides lastTechnicalProduct used by mix reference
   */
  private lastTechnicalProductMixReference(formIndex: number) {
    const technicalProductMixReference$ = this.store.select(getRmsRecipeSpecifications).pipe(
      take(1),
      filter((state) => !!state.length),
      map((form) => form[formIndex].values),
      switchMap((recipe) => {
        const productCode = recipe[SpecFieldNames.PRODUCT_SELECTION];
        return this.store.select(getOpenPlatformProducts).pipe(
          filter((availables) => !!availables.length),
          map((availables) => availables.find((available) => makeReference(available) === productCode)),
          map((productSelected) => productSelected.technicalProducts[0]),
          take(1),
        );
      }),
    );
    return technicalProductMixReference$;
  }

  /**
   * Provides lastTechnicalProduct used
   */
  private lastTechnicalProduct(formIndex: number) {
    const technicalProduct$ = this.store.select(getRmsRecipeSpecifications).pipe(
      take(1),
      filter((state) => !!state.length),
      map((form) => form[formIndex].values),
      switchMap((recipe) => {
        const keyName = `${_safe(() => recipe[SpecFieldNames.CONTRACT_SELECT])}_${_safe(
          () => recipe[SpecFieldNames.ID],
        )}`;
        return this.store.select(getOpenPlatformProductAddedValues(keyName)).pipe(
          // filter(availables => availables && availables.every(av => !av.loading)),
          map((availables) => {
            return (
              availables &&
              availables.filter((available) => {
                if (available.addedValues === null && available.technicalProduct === null) {
                  return false;
                } else {
                  return true;
                }
              })
            );
          }),
          map((availables) => _safe(() => availables[availables.length - 1])),
          map((last) => last && last.technicalProduct),
          withLatestFrom(
            this.store.select(getLegalEntity).pipe(
              map((legalEntity) => legalEntity.countryCode.trim()),
              take(1),
            ),
          ),
          map(([technicals, countryCode]) => {
            const unloadTypeValue = recipe[SpecFieldNames.UNLOAD_TYPE];
            const slumpValue = recipe[SpecFieldNames.SLUMP];
            const pumpabilityValue = recipe[SpecFieldNames.PUMPABILITY];
            const hasPump = isPump(unloadTypeValue);
            if (countryCode === COUNTRY_CODE.USA) {
              return technicals && technicals[0];
            }
            if (countryCode === COUNTRY_CODE.FRANCE) {
              if (!!hasPump) {
                return (
                  technicals &&
                  technicals.find(
                    (technical) =>
                      technical.isPumpable === hasPump &&
                      Number(technical['pumpable']) === pumpabilityValue &&
                      technical['slump']['slumpCode'] === slumpValue,
                  )
                );
              } else {
                return (
                  technicals &&
                  technicals.find(
                    (technical) =>
                      technical.isPumpable === false &&
                      technical['pumpable'] === '0' &&
                      technical['slump']['slumpCode'] === slumpValue,
                  )
                );
              }
            }
            // If the user selects Pump, needs a pumpable technical product.
            if (!!hasPump) {
              return technicals && technicals.find((technical) => technical.isPumpable === hasPump);
            }
            // If the user does not selects Pump,
            // Select the first technical product not pumpable as default.
            return technicals && technicals.find((technical) => technical.isPumpable === false);
          }),
          take(1),
        );
      }),
    );
    return technicalProduct$;
  }

  /**
   * Verify if the quantity is big enough to consider as a Big Order
   * @param formIndex
   * @returns
   */
  private isBigOrder(formIndex: number) {
    const bigOrder$ = this.store.select(getRmsRecipeSpecifications).pipe(
      filter((state) => !!state.length),
      take(1),
      map((form) => form[formIndex].values),
      switchMap((recipe) => {
        return this.store.select(getLocationPage).pipe(
          map((location) => {
            const quantity = recipe[SpecFieldNames.QUANTITY];
            const bigOrderQuantity = _safe(() => location.calendars.calendars[0].bigOrder.quantity);
            return quantity >= bigOrderQuantity ? true : false;
          }),
          take(1),
        );
      }),
    );
    return bigOrder$;
  }

  /**
   * Get the value added from the selection on the form (UI)
   * and prepend if the country has mandatory value added
   * @param formIndex
   * @returns string[]
   */
  private addedValues(formIndex: number): Observable<string[]> {
    return this.countryCode().pipe(
      switchMap((countryCode) => {
        if (countryCode === COUNTRY_CODE.COLOMBIA) {
          // we do not have added values in COLOMBIA RMX
          return of([]);
        }
        return this.store.select(recipeAndProducts).pipe(
          map(({ form, availableProducts }) => {
            const recipe = form[formIndex].values;
            const productCode = recipe[SpecFieldNames.PRODUCT_SELECTION];
            const product = availableProducts.find((available) => {
              return makeReference(available) === productCode;
            });
            return { recipe: recipe, product };
          }),
          map(({ recipe, product }) => {
            const families: string[] = [];
            const baseName = 'addedProduct';
            Object.keys(recipe).forEach((name) => {
              if (name.substr(0, baseName.length) === baseName) {
                const value: string = recipe[name];
                if (value) {
                  families.push(value);
                }
              }
            });
            return { families, product };
          }),
          map(({ families, product }) => this.makeAddedValuesFamiliesArray(families, product, countryCode)),
          take(1),
        );
      }),
    );
  }

  /**
   * Get the value added from the contract
   * and prepend if the country has mandatory value added
   * @param formIndex
   * @returns string[]
   */
  private addedValuesMixReference(formIndex: number): Observable<string[]> {
    return this.countryCode().pipe(
      switchMap((countryCode) => {
        return this.store.select(recipeAndProducts).pipe(
          map(({ form, availableProducts }) => {
            const recipe = form[formIndex].values;
            const productCode = recipe[SpecFieldNames.PRODUCT_SELECTION];
            const product = availableProducts.find((available) => {
              if ([COUNTRY_CODE.UNITED_KINGDOM, COUNTRY_CODE.FRANCE].includes(countryCode as COUNTRY_CODE)) {
                return makeReference(available) === productCode;
              } else {
                return available.productCode.replace(/^0+/, '') === productCode;
              }
            });
            return { recipe: recipe, product };
          }),
          map(({ recipe, product }) => {
            const families: string[] = [];
            product.contracts[0].valueAddeds.forEach((item) => families.push(item.valueAddedCode));
            return { families, product };
          }),
          map(({ families, product }) => this.makeAddedValuesFamiliesArray(families, product, countryCode)),
          take(1),
        );
      }),
    );
  }

  /**
   * Generates needed array for slots call for added values
   * Prepends default values if needed
   * @param families
   * @param product
   */
  private makeAddedValuesFamiliesArray(families: string[], product: ProductOP, country: string) {
    let preprendedVaps: string[] = [];
    const valueAddedSelected = !!families && families.length > 0;
    if (product['productXType'] === '0' && valueAddedSelected && country === COUNTRY_CODE.MEXICO) {
      preprendedVaps = prependedValueAdded(product);
    }
    const aux = [];
    preprendedVaps.forEach((vap) => {
      aux.push(vap);
    });
    families.forEach((family) => {
      aux.push(family);
    });
    return aux.filter((_) => !!_);
  }

  /**
   *
   * @param formIndex
   * @returns
   */
  private deliveryFrequency(formIndex: number): Observable<number> {
    return this.store.select(getRmsRecipeSpecifications).pipe(
      filter((state) => !!state.length),
      map((form) => form[formIndex].values),
      map((recipe) => recipe[SpecFieldNames.SPACING]),
      withLatestFrom(this.store.select(getActiveCountryCode)),
      switchMap(([value, countryCode]) => {
        const catalogSpacings = this.store.select(getCatalogsSelector).pipe(
          map((catalogs) => catalogs.find((catalog) => catalog.catalogCode === CATALOG_CODE.TIME_PER_LOAD)),
          map((record) =>
            record.entries.map(({ entryId, entryCode }) => {
              return { spacingId: entryId, spacingCode: entryCode };
            }),
          ),
        );
        const localSpacings = this.store.select(getLocalSpacings);
        const source = countryCode !== COUNTRY_CODE.COLOMBIA ? localSpacings : catalogSpacings;
        return source.pipe(
          map((spacings) =>
            spacings.find((spacing) => {
              return spacing.spacingId === value || spacing.spacingCode === value;
            }),
          ),
          map((spacing) => ({ spacing, countryCode })),
          take(1),
        );
      }),
      map(({ spacing, countryCode }) => {
        // USA: when spacingMinutes is explicitly 0, fall back to a default of 60.
        if (countryCode === COUNTRY_CODE.USA && Number(spacing['spacingMinutes']) === 0) {
          return 60;
        }
        return Number(spacing['spacingMinutes'] || spacing['spacingCode']);
      }),
      take(1),
    );
  }

  /**
   *
   * @returns
   */
  private shippingConditions(): Observable<string> {
    return this.store.select(getShippingCondition).pipe(
      map((shippingCondition) => shippingCondition.selected.shippingConditionCode),
      take(1),
    );
  }

  /**
   *
   * @param formIndex
   * @returns
   */
  private unloadingTime(formIndex: number): Observable<number> {
    return this.countryCode().pipe(
      switchMap((countryCode) => {
        return combineLatest([
          this.store.select(getRmsRecipeSpecifications).pipe(filter((state) => !!state.length)),
          this.store.select(getLocalSpacings),
        ]).pipe(
          take(1),
          map(([form, localSpacings]) => {
            const recipe = form[formIndex].values;
            if (countryCode === COUNTRY_CODE.USA) {
              // USA: resolve LOAD_SPACING (id/code) against getLocalSpacings to obtain the actual minutes; fall back to 60 when the value is 0/missing.
              const value = recipe[SpecFieldNames.SPACING];
              const matchedSpacing = (localSpacings || []).find((spacing) => spacing.spacingId === value);
              const spacingMinutes = matchedSpacing ? Number(matchedSpacing['spacingMinutes']) : 0;
              return spacingMinutes !== 0 ? spacingMinutes : 60;
            }
            // TO-DO: Review what will happen to this field for COMMAND EXPERIENCE
            const code = recipe[SpecFieldNames.DISCHARGE_TIME] as string;
            if (!code) {
              return Number('');
            }
            return Number(code.substring(code.indexOf('-') + 1, code.length - 2));
          }),
        );
      }),
    );
  }

  /**
   *
   * @param formIndex
   * @returns
   */
  private requestType(formIndex: number): Observable<RMS_REQUEST_TYPES> {
    const type$ = this.hasCompanyPump(formIndex).pipe(
      map((hasCompanyPump) => {
        if (hasCompanyPump) {
          return RMS_REQUEST_TYPES.CONCRETE_PUMP;
        } else {
          return RMS_REQUEST_TYPES.CONCRETE;
        }
      }),
    );
    return type$;
  }

  /**
   *
   * @param formIndex
   * @returns
   */
  private pump(formIndex: number) {
    return this.hasCompanyPump(formIndex).pipe(
      switchMap((hasCompanyPump) => {
        return iif(
          () => hasCompanyPump,
          this.pumpForm(formIndex), // if hasCompanyPump returns true
          of({
            minLength: 0,
            maxLength: 0,
            type: [],
            pumpingTime: 0,
            installTime: 0,
            pumpWashOnSite: false,
            hoseQty: 0,
            pipeQty: 0,
            crewQty: 0,
          }), // if hasCompanyPump returns false
        );
      }),
    );
  }

  /**
   * Retrieves and processes pump form data based on the provided form index.
   *
   * This function selects the RMS recipe specifications from the store, filters the state to ensure it has data,
   * and then maps the form data to extract the values at the specified form index. It then switches to another
   * observable to get the first calendar state and performs additional operations to calculate pump parameters.
   *
   * The `maxLength` conditional checks if the country code is Mexico. If it is, it uses the `recipe.pumpLength` value.
   * Otherwise, it uses the value from the additional parameters.
   *
   * @param {number} formIndex - The index of the form to retrieve and process.
   * @returns {Observable<any>} An observable that emits the processed pump form data.
   */
  private pumpForm(formIndex: number): Observable<any> {
    return this.store.select(getRmsRecipeSpecifications).pipe(
      filter((state) => !!state.length),
      take(1),
      map((form) => form[formIndex].values),
      switchMap((recipe) =>
        this.store.select(getFirstCalendar).pipe(
          withLatestFrom(this.store.select(getActiveExperienceCode), this.countryCode()),
          switchMap(([state, activeExperienceCode, country]) => {
            const pumpCapacity = recipe[SpecFieldNames.PUMP];
            const unloadType = recipe[SpecFieldNames.UNLOAD_TYPE];
            if (activeExperienceCode === ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK && country === COUNTRY_CODE.MEXICO) {
              return this.pumpAdditionalParametersCalendars(unloadType, pumpCapacity);
            }
            return this.pumpAdditionalParameters(pumpCapacity).pipe(
              switchMap((additionalParameters) => {
                const pumpInstallTime = _safe(() => state.pump.pumpInstallTime);
                const pipeInstallTime = _safe(() => state.pump.pipeInstallTime);
                const pipeQuantity = recipe[SpecFieldNames.PIPE_QUANTITY];
                const hoses = recipe[SpecFieldNames.HOSES];
                const calculatedInstallationTime =
                  pumpInstallTime !== undefined && pipeInstallTime !== undefined
                    ? pumpInstallTime + pipeInstallTime * (pipeQuantity + hoses)
                    : 30;

                return this.store.select(getActiveExperienceCodeAndCountryCode).pipe(
                  map(({ experienceCode, countryCode }) => {
                    let maxLength;
                    if (
                      countryCode === COUNTRY_CODE.MEXICO &&
                      experienceCode === ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK
                    ) {
                      maxLength = recipe[SpecFieldNames.PUMP_LENGTH];
                    } else {
                      maxLength = _safe(
                        () => additionalParameters.find((a) => a.key === ADDITIONAL_PARAMETERS.MAX_LENGTH).value,
                      );
                    }
                    return {
                      minLength: _safe(
                        () => additionalParameters.find((a) => a.key === ADDITIONAL_PARAMETERS.MIN_LENGTH).value,
                      ),
                      maxLength: maxLength,
                      type: _safe(() => [additionalParameters.find((a) => a.key === ADDITIONAL_PARAMETERS.TYPE).value]),
                      hoseQty: +hoses,
                      pipeQty: +pipeQuantity,
                      crewQty: 0,
                      installTime: calculatedInstallationTime,
                    };
                  }),
                );
              }),
            );
          }),
          take(1),
        ),
      ),
    );
  }

  /**
   *
   * @param entryCode
   * @returns
   */
  private pumpAdditionalParameters(entryCode: string): Observable<AdditionalParameters[]> {
    return this.store.select(getSpecificationForm).pipe(
      take(1),
      map((state) => state.catalogs.find((catalog) => catalog.catalogCode === CATALOG_CODE.PUMP_CAPACITY)),
      map((catalog) => _safe(() => catalog.entries.find((entry) => entry.entryCode === entryCode))),
      map((entry) => _safe(() => entry.additionalParameters)),
    );
  }

  /**
   * function to get the additionalParameter of pumping { ListPumpPen }
   * now on RMX UL 5.2 that that is from calendars v2
   * @param unloadMethodCode
   * @param pumpSizeCode
   * @returns {Observable<any>}
   */
  private pumpAdditionalParametersCalendars(unloadMethodCode: string, pumpSizeCode: string): Observable<any> {
    return this.store.select(getFirstCalendar).pipe(
      withLatestFrom(this.store.select(getRmsSpecificationFormState)),
      take(1),
      map(([state, recipeState]) => {
        const recipe = _safe(() => recipeState.forms[0].values);
        const common = this.commonPumpParameters(recipe, state);
        if (unloadMethodCode === BOOM_PUMP) {
          const from = state.pump.unloadMethods.find((method) => method.unloadMethodCode === BOOM_PUMP_CODE);
          const selected = from.listPumpPen.find((pumpSize) => pumpSize.pumpSizeCode === pumpSizeCode);
          const minLength = selected.flagSpecificSize ? recipe[SpecFieldNames.PUMP_LENGTH] - 1 : selected.longMin;
          const maxLength = selected.flagSpecificSize ? +recipe[SpecFieldNames.PUMP_LENGTH] : selected.longMax;
          const type = selected.vehicleTypeCode;
          const pumpObject = { minLength: minLength, maxLength: maxLength, type: [type], ...common };
          return pumpObject;
        }
        if (unloadMethodCode === STATIONARY_PUMP) {
          const from = state.pump.unloadMethods.find((method) => method.unloadMethodCode === STATIONARY_PUMP_CODE);
          const selected = from.listPumpStationary.find((pumpSize) => pumpSize.pumpSizeCode === pumpSizeCode);
          const minLength = selected.longMin;
          const maxLength = selected.longMax;
          const type = selected.prefixFull;
          const pumpObject = { minLength: minLength, maxLength: maxLength, type: [type], ...common };
          return pumpObject;
        }
        return common;
      }),
    );
  }

  /**
   * True if the company pump was selected
   * @param formIndex
   * @returns
   */
  private hasCompanyPump(formIndex: number): Observable<boolean> {
    return this.store.select(getRmsRecipeSpecifications).pipe(
      filter((state) => !!state.length),
      take(1),
      map((form) => form[formIndex].values),
      map((recipe) => !!recipe[SpecFieldNames.PUMP]),
    );
  }

  private getOrderCode(): Observable<string> {
    return this.store.select(getOrderSelectedDetail).pipe(
      map((order) => {
        return !order ? '0' : order.orderCode;
      }),
      take(1),
    );
  }

  private isModifyOrder(): Observable<boolean> {
    return this.store.select(getRmsSpecificationsIsModify).pipe(take(1));
  }

  private getProgrammedDateTime(): Observable<any> {
    return this.store.select(getOrderSelectedDetail).pipe(
      map((order) => (order ? order.programmedDateTime : null)),
      take(1),
    );
  }

  /**
   *
   * @returns
   */
  private salesOrgCode(): Observable<string> {
    return this.store.select(getLocationPage).pipe(
      withLatestFrom(this.experiencesService.commandDcoEnabled$()),
      map(([state, isCommandDcoEnabled]) => {
        if (!isCommandDcoEnabled && state.jobsite.address.countryCode === COUNTRY_CODE.USA) {
          return '2037';
        }
        return state.jobsite.jobsiteFullSalesArea.salesOrganizationCode.replace(/^0+/, '');
      }),
      take(1),
    );
  }

  /**
   *
   * @returns
   */
  private username(): Observable<string> {
    const username$ = this.store.select(getAuth).pipe(
      take(1),
      map((auth) => {
        return auth.profile.userAccount;
      }),
    );
    return username$;
  }

  /**
   * Obtains from state the country code of the active Legal Entity
   * @returns value of the country code as a string
   */
  private countryCode(): Observable<string> {
    return this.store.select(getLegalEntity).pipe(
      map((legalEntity) => legalEntity.countryCode.trim()),
      take(1),
    );
  }

  /**
   * Obtains from state the Cutoff value
   * @returns value of the cuttoff as a string
   */
  private getCutOff(): Observable<string> {
    return this.store.select(getCutOff).pipe(
      map((response) => response.dateAvailable),
      take(1),
    );
  }

  /**
   * Retrieves the offer days and business days based on the location configuration
   * and the current experience code. Returns an object with the number of offer days
   * and business days (if enabled by a feature flag).
   *
   * @returns An observable that emits an object with:
   *   - `offerDays`: The number of offer days, adjusted according to the calendar configuration.
   *   - `businessDays`: An array of business days (`BusinessDay[]`) or `null` if not enabled.
   *
   * The `offerDays` is calculated based on the experience code and the activation of a feature
   * flag that enables business days. If an error occurs while retrieving the data, `offerDays`
   * defaults to a value of 29 days.
   */
  private getOfferAndBusinessDay(): Observable<{ offerDays: number; businessDays: BusinessDay[] | null }> {
    const experienceAndCountryCode$ = this.store.select(getActiveExperienceCodeAndCountryCode);
    const locationPage$ = this.store.select(getLocationPage).pipe(
      filter((locationState) => {
        return locationState != null && locationState.calendars != null && locationState.calendars.calendars.length > 0;
      }),
    );
    const offerDays$ = combineLatest([experienceAndCountryCode$, locationPage$]).pipe(
      switchMap(([{ experienceCode }, locationState]) => {
        let offerDays: number;
        let businessDays: BusinessDay[] = null;
        const enabledBusinessDays = this.featureToggle.isFeatureEnabled(Features.EnabledBusinessDays);
        try {
          if (
            enabledBusinessDays &&
            (experienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK ||
              experienceCode === ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK ||
              experienceCode === ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK)
          ) {
            businessDays = locationState.calendars.calendars[0].businessDays;
            offerDays = locationState.calendars.calendars[0].offerDays - 1;
          } else {
            offerDays = locationState.calendars.calendars[0].offerDays - 1;
          }
        } catch (e) {
          offerDays = 30 - 1;
        }
        return of({ offerDays, businessDays });
      }),
      take(1),
    );
    return offerDays$;
  }

  /**
   * Creates and observable to get from the store the cut off value and the offer days value.
   * @returns An Observable with the cut off value as string and offer days as a number.
   */
  private getCutOffAndOfferDays(): Observable<{
    cutOffString: string;
    offerDays: number;
    businessDays?: BusinessDay[];
  }> {
    const cutoff$ = this.store.select(getCutOff);
    const getOfferAndBusinessDay$ = this.getOfferAndBusinessDay();
    const getCutoffTemperature$ = this.store.select(getCutOffTemperatureState);
    const enabledBusinessDays = this.featureToggle.isFeatureEnabled(Features.EnabledBusinessDays);
    const enabledBusinessDaysCutOff = this.featureToggle.isFeatureEnabled(Features.EnableBusinessDaysCutOff);
    const minMaxDates$ = combineLatest([cutoff$, getOfferAndBusinessDay$, getCutoffTemperature$]).pipe(
      map(([cutOff, { offerDays, businessDays }, cutoffTemperature]) => {
        let cutOffString: string;
        if (cutoffTemperature.isTemperatureControl && cutoffTemperature.cutOff !== null) {
          const time = cutoffTemperature.cutOff.timeCut.replace('.000Z', '');
          cutOffString = cutoffTemperature.cutOff.dateAvailable + 'T' + time;
        } else if (cutOff !== null) {
          if (enabledBusinessDays && enabledBusinessDaysCutOff) {
            const businessDaysList = Array.isArray(businessDays) ? businessDays : [];
            const firstValidBusinessDay =
              businessDaysList.find((item) => item.type === BusinessDayType.Ok) || businessDaysList[0];
            if (firstValidBusinessDay && firstValidBusinessDay.date) {
              cutOffString = moment(firstValidBusinessDay.date).format('YYYY-MM-DD');
            } else {
              let time = cutOff.timeCut.replace('.000Z', '');
              const parts = time.split(':');
              if (parts.length === 4) {
                time = parts.slice(0, 3).join(':');
              }
              cutOffString = cutOff.dateAvailable + 'T' + time;
            }
          } else {
            let time = cutOff.timeCut.replace('.000Z', '');
            const parts = time.split(':');
            if (parts.length === 4) {
              time = parts.slice(0, 3).join(':');
            }
            cutOffString = cutOff.dateAvailable + 'T' + time;
          }
        }
        return { cutOffString, offerDays, businessDays, cutOff };
      }),
    );
    return minMaxDates$;
  }

  private filterPlantsScheduleDataWithRangeLimits(
    plantsSchedule: PlantsScheduleOP[],
    cutOffString: string,
    offerDays: number,
    maxBusinessDays?: string,
  ): PlantsScheduleOP[] {
    const filteredPlantsSchedule: PlantsScheduleOP[] = [];
    if (!plantsSchedule || plantsSchedule.length === 0) {
      return filteredPlantsSchedule;
    }
    const minMoment = moment(cutOffString);
    const maxMoment = maxBusinessDays ? moment(maxBusinessDays) : minMoment.clone().add(offerDays, 'days');
    plantsSchedule.forEach((schedule, index) => {
      // validar que la fecha plant schedule quede entre el cutoff y el cutoff + offerdays
      const scheduleDateMoment = moment(schedule.date);
      if (
        (scheduleDateMoment.isSame(minMoment, 'day') || scheduleDateMoment.isAfter(minMoment, 'day')) &&
        (scheduleDateMoment.isSame(maxMoment, 'day') || scheduleDateMoment.isBefore(maxMoment, 'day'))
      ) {
        // schedule Date is between the cuttoff date and the offerdays addition
        filteredPlantsSchedule.push(schedule);
      }
    });
    return filteredPlantsSchedule;
  }

  private filterConcreteProposalsDataWithRangeLimits(
    concreteProposals: ConcreteProposalOP[],
    cutOffString: string,
    offerDays: number,
    maxBusinessDays?: string,
  ): ConcreteProposalOP[] {
    const filteredConcreteProposals: ConcreteProposalOP[] = [];
    if (concreteProposals.length === 0) {
      return filteredConcreteProposals;
    }
    const minMoment = moment(cutOffString);
    const maxMoment = maxBusinessDays ? moment(maxBusinessDays) : minMoment.clone().add(offerDays, 'days');
    concreteProposals.forEach((proposal) => {
      const proposalHourMinute = proposal.arriveAtText.split(':');
      const proposalMoment = moment(proposal.deliveryDate).set({
        hour: Number(proposalHourMinute[0]),
        minute: Number(proposalHourMinute[1]),
      });
      if (
        (proposalMoment.isSame(minMoment, 'day') || proposalMoment.isAfter(minMoment, 'day')) &&
        (proposalMoment.isBefore(maxMoment, 'day') || proposalMoment.isSame(maxMoment, 'day'))
      ) {
        filteredConcreteProposals.push(proposal);
      }
    });
    return filteredConcreteProposals;
  }

  /**
   * Usa la informacion del horario de plantas y propuestas de entrega para armar
   * el objeto de parametro que acepta el componente de cwc time slots.
   * Valida que las fechas sean posterior al cutoff y dentro de los dias de oferta.
   * @param plantsSchedule Horarios de plantas disponibles.
   * @param concreteProposals Propuetas de entrega de la orden.
   * @param cutOffString El valor del cutoff como string.
   * @param offerDays Numero de los dias de oferta permitidos.
   * @returns Objeto que se puede utilizar con cwc time slots.
   */
  private formatDataForFullScheduleVisibility(
    plantsSchedule: PlantsScheduleOP[],
    concreteProposals: ConcreteProposalOP[],
    cutOffString: string,
    offerDays: number,
    businessDays?: BusinessDay[],
  ): CwcTimeSlot[] {
    let cwcTimeSlots: CwcTimeSlot[] = [];
    if (concreteProposals.length === 0 || plantsSchedule.length === 0) {
      return cwcTimeSlots;
    }
    const minMoment = moment(cutOffString);
    const businessDaysFeatureEnabled = this.featureToggle.isFeatureEnabled(Features.EnabledBusinessDays);
    let maxMoment: moment.Moment;
    if (businessDays && businessDaysFeatureEnabled) {
      maxMoment = moment(businessDays[businessDays.length - 1].date);
    } else {
      maxMoment = minMoment.clone().add(offerDays, 'days');
    }
    const dayNamesString: string = this.translate.instant('global.dayNames');
    const dayNames = dayNamesString.split(',');
    const countryCodeString = this.country.getCountryCodeProperty() as COUNTRY_CODE;
    const displayDateFormat = countryCodeString === COUNTRY_CODE.UNITED_KINGDOM ? 'DD MMM, YYYY' : 'MMM DD, YYYY';
    // navigate callback response to format the data for the cwc components
    plantsSchedule.forEach((schedule, index) => {
      // validar que la fecha plant schedule quede entre el cutoff y el cutoff + offerdays
      const scheduleDateMoment = moment(schedule.date);
      if (
        (scheduleDateMoment.isSame(minMoment, 'day') || scheduleDateMoment.isAfter(minMoment, 'day')) &&
        (scheduleDateMoment.isSame(maxMoment, 'day') || scheduleDateMoment.isBefore(maxMoment, 'day'))
      ) {
        // schedule Date is between the cuttoff date and the offerdays addition
        const scheduleStartHourMinute = schedule.start.split(':');
        const scheduleStartMoment = moment(schedule.date).set({
          hour: Number(scheduleStartHourMinute[0]),
          minute: Number(scheduleStartHourMinute[1]),
        });
        const scheduleCloseHourMinute = schedule.close.split(':');
        const scheduleCloseMoment = moment(schedule.date).set({
          hour: Number(scheduleCloseHourMinute[0]),
          minute: Number(scheduleCloseHourMinute[1]),
        });
        const dayDisplayString = dayNames[scheduleDateMoment.day()];
        cwcTimeSlots.push({
          dateMoment: scheduleDateMoment.clone(),
          dayDisplay: dayDisplayString,
          dateDisplay: scheduleDateMoment.clone().format(displayDateFormat),
          plantOpeningTime: scheduleStartMoment,
          plantClosingTime: scheduleCloseMoment,
          schedule: [],
        });
      }
    });
    // fill gaps in plants' days
    if (cwcTimeSlots.length >= 2) {
      let i = 0;
      while (i < cwcTimeSlots.length) {
        const currentItem = cwcTimeSlots[i];
        const j = i + 1;
        if (j < cwcTimeSlots.length) {
          const next = cwcTimeSlots[j];
          const daysDiff = next.dateMoment.diff(currentItem.dateMoment, 'days');
          if (daysDiff > 1) {
            // theres a gap of dates in between plants' schedules
            const remainder = cwcTimeSlots.splice(j);
            let daysToSum = 1;
            while (daysToSum < daysDiff) {
              const missingDate = currentItem.dateMoment.clone().add(daysToSum, 'days');
              const dayDisplayString = dayNames[missingDate.day()];
              const newTimeSlot: CwcTimeSlot = {
                dateMoment: missingDate,
                dayDisplay: dayDisplayString,
                dateDisplay: missingDate.format(displayDateFormat),
                plantOpeningTime: missingDate,
                plantClosingTime: missingDate,
                schedule: [],
              };
              cwcTimeSlots.push(newTimeSlot);
              daysToSum++;
            }
            cwcTimeSlots = cwcTimeSlots.concat(remainder);
          }
        }
        i++;
      }
    }
    // check the schedule times
    concreteProposals.forEach((proposal) => {
      const proposalSlot = { time: proposal.arriveAtText, warning: false, plant: proposal.plant };
      const proposalHourMinute = proposal.arriveAtText.split(':');
      const proposalMoment = moment(proposal.deliveryDate).set({
        hour: Number(proposalHourMinute[0]),
        minute: Number(proposalHourMinute[1]),
      });
      if (
        (proposalMoment.isSame(minMoment, 'day') || proposalMoment.isAfter(minMoment, 'day')) &&
        (proposalMoment.isBefore(maxMoment, 'day') || proposalMoment.isSame(maxMoment, 'day'))
      ) {
        const foundIndex = cwcTimeSlots.findIndex((timeSlot) => {
          if (timeSlot.dateMoment.isSame(proposalMoment, 'day')) {
            return true;
          }
          return false;
        });
        if (foundIndex > -1) {
          cwcTimeSlots[foundIndex].schedule.push(proposalSlot);
          if (cwcTimeSlots[foundIndex].plantCode !== proposal.plant) {
            cwcTimeSlots[foundIndex].plantCode = proposal.plant;
            // CX024GO2005-8844 check this plant's schedule
          }
        }
      }
    });
    cwcTimeSlots.forEach((timeSlot) => {
      timeSlot.schedule.sort((a, b) => {
        const aHourMinute = a.time.split(':');
        const bHourMinute = b.time.split(':');
        const aTime = moment().set({
          hour: Number(aHourMinute[0]),
          minute: Number(aHourMinute[1]),
        });
        const bTime = moment().set({
          hour: Number(bHourMinute[0]),
          minute: Number(bHourMinute[1]),
        });
        if (aTime.isBefore(bTime, 'minute')) {
          return -1;
        }
        if (bTime.isBefore(aTime, 'minute')) {
          return 1;
        }
        return 0;
      });
    });
    return cwcTimeSlots;
  }

  private isOrderWithPumpPrimer(formIndex: number): Observable<boolean> {
    const getPumpPrimerState$ = this.store.select(getPumpPrimerState);
    const getRmsSpecificationForms$ = this.store.select(getRmsRecipeSpecifications);
    return combineLatest([getPumpPrimerState$, getRmsSpecificationForms$]).pipe(
      take(1),
      map(([_pumpPrimerState, _forms]) => {
        const recipe = _forms[formIndex];
        const unloadTypeValue = recipe.values[SpecFieldNames.UNLOAD_TYPE];
        const unloadTypePumpValues = _safe(() => _pumpPrimerState.unloadTypePumpValues, undefined);
        const pumpPrimerMixValue = recipe.values[PUMP_PRIMER_MIX];
        return isOrderWithPumpPrimerProduct(pumpPrimerMixValue, unloadTypeValue, unloadTypePumpValues);
      }),
    );
  }

  /**
   * Generates common pump fields
   *
   * hosesQty, pipeQty, crewQty, installTime
   * @param recipe
   * @param state
   * @returns
   */
  private commonPumpParameters(recipe, state) {
    const pumpInstallTime = _safe(() => state.pump.pumpInstallTime);
    const pipeInstallTime = _safe(() => state.pump.pipeInstallTime);
    let pipeQuantity = _safe(() => recipe[SpecFieldNames.PIPE_QUANTITY], 0);

    // If linear meters set, redefine pipes
    if (state.pump.flagLinearMeters) {
      pipeQuantity = Math.ceil((recipe[SpecFieldNames.LINEAR_METERS] || 0) / 3);
    }

    const hoses = _safe(() => recipe[SpecFieldNames.HOSES], 0);
    const calculatedInstallationTime =
      pumpInstallTime !== undefined && pipeInstallTime !== undefined
        ? pumpInstallTime + pipeInstallTime * (pipeQuantity + hoses)
        : 30;

    state.pump.flagLinearMeters;
    return {
      hoseQty: +hoses,
      pipeQty: +pipeQuantity,
      crewQty: 0,
      installTime: calculatedInstallationTime,
    };
  }

  /**
   * Gets the experience configuration based on the active experience code.
   * @param activeExperienceCode - The active experience code
   * @returns Configuration object with experience flags
   */
  private getExperienceConfiguration(activeExperienceCode: string) {
    const isCommand =
      activeExperienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK ||
      activeExperienceCode === ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK;
    const isRMXUL = activeExperienceCode === ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK;
    const isArkik = activeExperienceCode === ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK;

    return { isCommand, isRMXUL, isArkik };
  }

  /**
   * Gets the API configuration based on country and experience settings.
   * @param isUsa - Whether the country is USA
   * @param isMX - Whether the country is Mexico
   * @param experienceConfig - The experience configuration
   * @returns API configuration object
   */
  private getApiConfiguration(isUsa: boolean, isMX: boolean, experienceConfig: any) {
    const useV2SlotApi =
      (isUsa && experienceConfig.isCommand) ||
      (isMX && experienceConfig.isRMXUL) ||
      (isUsa && experienceConfig.isArkik);

    return { useV2SlotApi };
  }

  /**
   * Processes Arkik-specific configuration for the parameter.
   * @param parameter - The parameter object to modify
   * @param isArkik - Whether the experience is Arkik
   */
  private processArkikConfiguration(parameter: any, isArkik: boolean) {
    if (isArkik) {
      parameter.orderCode = parameter.orderCode;
      parameter.jobsite.jobsiteCode = this.removeLeadingZeros(parameter.jobsite.jobsiteCode);
    }
  }

  /**
   * Processes temperature control configuration.
   * @param parameter - The parameter object to modify
   * @param recipe - The recipe object
   * @param useV2SlotApi - Whether to use V2 slot API
   * @param experienceConfig - The experience configuration
   * @param data - The data object
   * @param formState - The form state
   */
  private processTemperatureControl(
    parameter: any,
    recipe: any,
    useV2SlotApi: boolean,
    experienceConfig: any,
    data: any,
    formState: any,
  ) {
    const temperatureValue = !!recipe.values[SpecFieldNames.ICE_QUANTITY]
      ? recipe.values[SpecFieldNames.ICE_QUANTITY]
      : recipe.values[SpecFieldNames.TEMPERATURE_UNDER];

    if (useV2SlotApi && !!temperatureValue && temperatureValue !== 'NONE') {
      const refrigerant = _safe(
        () => getRefrigerantValueAdded(data.vaps[data.data.slot.formIndex], data.calendar?.tempControlFlag),
        undefined,
      );
      if (!!refrigerant) {
        parameter.products[0].valueAddedCodes.push(refrigerant.valueAddedCode.replace(/^(0+)/g, ''));
      }
      if (!!recipe.values[SpecFieldNames.SUMMER_SET]) {
        parameter.products[0].valueAddedCodes.push(recipe.values[SpecFieldNames.SUMMER_SET].replace(/^(0+)/g, ''));
      }
      parameter['controlTemperature'] = true;
    } else {
      if (!!recipe.values[SpecFieldNames.SUMMER_SET]) {
        parameter.products[0].valueAddedCodes.push(recipe.values[SpecFieldNames.SUMMER_SET].replace(/^(0+)/g, ''));
      }
    }

    if (
      experienceConfig.isCommand &&
      formState.isModify &&
      !!recipe.values[SpecFieldNames.HAS_TEMPERATURE_MANAGEMENT]
    ) {
      parameter['modifycontrolTemperature'] = true;
    } else {
      parameter['modifycontrolTemperature'] = false;
    }
  }

  /**
   * Processes pump primer configuration.
   * @param parameter - The parameter object to modify
   * @param recipe - The recipe object
   * @param data - The data object
   */
  private processPumpPrimerConfiguration(parameter: any, recipe: any, data: any) {
    if (data.isOrderWithPumpPrimer) {
      parameter.products[0]['pouringMethod'] = true;
      parameter.products[0]['primeProduct'] = recipe.values[PUMP_PRIMER_MIX];
      parameter.products[0]['primeVolume'] = recipe.values[PUMP_PRIMER_QUANTITY];
      parameter.products[0]['primeFrequency'] = recipe.values[PUMP_PRIMER_ARRIVAL_BEFORE_DELIVERY];
    }
  }

  /**
   * Processes pouring method configuration based on unload type.
   * @param parameter - The parameter object to modify
   * @param recipe - The recipe object
   * @param data - The data object
   */
  private processPouringMethodConfiguration(parameter: any, recipe: any, data: any) {
    const unloadType = recipe.values[SpecFieldNames.UNLOAD_TYPE];
    const pumpPrimerValues = data.pumpPrimerState.unloadTypePumpValues;
    parameter.products[0]['pouringMethod'] = isUnloadTypePumpValue(unloadType, pumpPrimerValues);
  }

  /**
   * Processes cut-off configuration for slots.
   * @param parameter - The parameter object to modify
   * @param isUsa - Whether the country is USA
   * @param useV2SlotApi - Whether to use V2 slot API
   * @param data - The data object
   * @param cutOffState - The cut-off state
   */
  private processCutOffConfiguration(
    parameter: any,
    isUsa: boolean,
    useV2SlotApi: boolean,
    data: any,
    cutOffState: any,
  ) {
    if (isUsa && useV2SlotApi && data.OfferDay && data.OfferDay.cutOffString) {
      const paramValue = calculateCutOffIndicator(cutOffState);
      parameter.products.forEach((product) => {
        product.availability.cutOffTime = paramValue;
      });
    }
  }

  /**
   * Processes return loads configuration for Mexico.
   * @param parameter - The parameter object to modify
   * @param isMX - Whether the country is Mexico
   * @param recipe - The recipe object
   */
  private processReturnLoads(parameter: any, isMX: boolean, recipe: any) {
    if (isMX && !!recipe.values[SpecFieldNames.PUMP]) {
      parameter['returnloads'] = true;
    }
  }
}
